import { isMerchantRole, type MerchantRole } from './rbac';

interface JwtPayload {
  role?: unknown;
  exp?: unknown;
}

function decodeBase64Url(value: string): string | null {
  const normalized = value.replace(/-/g, '+').replace(/_/g, '/');
  const padding = normalized.length % 4;
  const padded = normalized + (padding === 0 ? '' : '='.repeat(4 - padding));

  try {
    if (typeof atob === 'function') {
      return atob(padded);
    }

    if (typeof Buffer !== 'undefined') {
      return Buffer.from(padded, 'base64').toString('utf-8');
    }
  } catch {
    return null;
  }

  return null;
}

function getJwtPayload(token: string | null | undefined): JwtPayload | null {
  if (!token) {
    return null;
  }

  const parts = token.split('.');
  if (parts.length < 2 || !parts[1]) {
    return null;
  }

  const decodedPayload = decodeBase64Url(parts[1]);
  if (!decodedPayload) {
    return null;
  }

  try {
    return JSON.parse(decodedPayload) as JwtPayload;
  } catch {
    return null;
  }
}

export function getRoleClaimFromJwt(token: string | null | undefined): MerchantRole | null {
  const payload = getJwtPayload(token);
  if (!payload || !isMerchantRole(payload.role)) {
    return null;
  }

  return payload.role;
}

export function isJwtExpired(token: string | null | undefined, clockSkewSeconds = 15): boolean {
  const payload = getJwtPayload(token);
  if (!payload) {
    return true;
  }

  if (typeof payload.exp === 'undefined' || payload.exp === null) {
    return false;
  }

  const exp =
    typeof payload.exp === 'number'
      ? payload.exp
      : typeof payload.exp === 'string'
        ? Number(payload.exp)
        : Number.NaN;

  if (!Number.isFinite(exp)) {
    return true;
  }

  const nowEpochSeconds = Math.floor(Date.now() / 1000);
  return exp <= nowEpochSeconds + clockSkewSeconds;
}
